import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js'
import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { supabase } from '../services/supabaseClient'
import { deleteAccount } from '../services/supabaseRepo'
import { trackCompleteRegistration } from '../utils/facebookPixel'
import { setGAUserId, trackGASignUp } from '../utils/googleAnalytics'
import { isInitialSignIn } from '../utils/registration'
import { trackLinkedInRegistration } from '../utils/linkedinTracking'
import { generateEventId } from '../utils/trackingCookies'

export type AuthContextValue = {
  // State
  sessionUserId: string | null
  sessionEmail: string | null
  avatarUrl: string | null
  isAuthModalOpen: boolean
  isAuthenticated: boolean
  isAuthReady: boolean
  newsletterPromptEmail: string | null

  // Actions
  setIsAuthModalOpen: (open: boolean) => void
  dismissNewsletterPrompt: () => void
  handleLogout: () => Promise<void>
  handleDeleteAccount: (email: string, reason: string, details?: string) => Promise<void>
  setAvatarFromPath: (path: string | null) => void
  refreshAvatar: () => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}
const AVATAR_BUCKET = env.VITE_SUPABASE_AVATAR_BUCKET ?? 'avatars'

type AuthProviderProps = {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [sessionUserId, setSessionUserId] = useState<string | null>(null)
  const [sessionEmail, setSessionEmail] = useState<string | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [isAuthReady, setIsAuthReady] = useState(false)
  const [newsletterPromptEmail, setNewsletterPromptEmail] = useState<string | null>(null)
  const promptedNewsletterUserIds = useRef(new Set<string>())

  const resolveAvatarUrl = useCallback((path: string | null) => {
    if (!path) return null
    const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path)
    return data.publicUrl ?? null
  }, [])

  const setAvatarFromPath = useCallback(
    (path: string | null) => {
      setAvatarUrl(resolveAvatarUrl(path))
    },
    [resolveAvatarUrl]
  )

  const applyAvatarFromUser = useCallback(
    (user: User | null | undefined) => {
      const meta = (user?.user_metadata ?? {}) as { avatar_path?: unknown }
      const path = typeof meta.avatar_path === 'string' ? meta.avatar_path : null
      setAvatarFromPath(path)
    },
    [setAvatarFromPath]
  )

  const refreshAvatar = useCallback(async () => {
    const { data } = await supabase.auth.getUser()
    applyAvatarFromUser(data.user)
  }, [applyAvatarFromUser])

  const handleLogout = useCallback(async () => {
    try {
      await supabase.auth.signOut()
    } catch (err) {
      console.warn('signOut failed', err)
    }
    setSessionUserId(null)
    setGAUserId(null)
    setSessionEmail(null)
    setAvatarUrl(null)
    // Full page navigation so Astro serves the landing page
    document.cookie = 'jt_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
    window.location.href = '/'
  }, [])

  const handleDeleteAccount = useCallback(
    async (email: string, reason: string, details?: string) => {
      await deleteAccount(email, reason, details)
      await handleLogout()
    },
    [handleLogout]
  )

  const dismissNewsletterPrompt = useCallback(() => {
    setNewsletterPromptEmail(null)
  }, [])

  // Cookie management for Astro SSR routing
  // This cookie is a UI hint only - not an auth token
  const setSessionHintCookie = useCallback((hasSession: boolean) => {
    if (hasSession) {
      document.cookie = 'jt_session=1; path=/; max-age=31536000; SameSite=Lax'
      return
    }
    document.cookie = 'jt_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
  }, [])

  // Auth state initialization and listener
  useEffect(() => {
    let isMounted = true
    const init = async () => {
      // Handle PKCE code exchange from OAuth callback (e.g. Google sign-in)
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      if (code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
        if (exchangeError) {
          console.error('OAuth code exchange error', exchangeError)
        }
        // Clean auth callback params from the URL while preserving other params/hash
        const url = new URL(window.location.href)
        url.searchParams.delete('code')
        url.searchParams.delete('state')
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
      }

      const { data, error } = await supabase.auth.getSession()
      if (!isMounted) {
        return
      }
      if (error) {
        console.error('getSession error', error)
        setSessionHintCookie(false)
        setSessionUserId(null)
        setSessionEmail(null)
        setAvatarUrl(null)
      } else {
        setSessionHintCookie(Boolean(data.session))
        const user = data.session?.user
        if (user) setGAUserId(user.id)
        setSessionUserId(user?.id ?? null)
        setSessionEmail(user?.email ?? null)
        applyAvatarFromUser(user)
      }
      setIsAuthReady(true)
    }
    init()

    const { data: sub } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, sess: Session | null) => {
        if (!isMounted) {
          return
        }
        const user = sess?.user
        if (user || event === 'SIGNED_OUT') setGAUserId(user?.id ?? null)
        setSessionHintCookie(Boolean(sess))
        setSessionUserId(user?.id ?? null)
        setSessionEmail(user?.email ?? null)
        applyAvatarFromUser(user)

        setIsAuthReady(true)

        // Track new registration for Meta Pixel
        if (event === 'SIGNED_IN' && user) {
          const isNewUser = isInitialSignIn(user)
          if (isNewUser && !promptedNewsletterUserIds.current.has(user.id)) {
            promptedNewsletterUserIds.current.add(user.id)
            const provider = user.app_metadata?.provider as string | undefined
            const method =
              provider === 'google'
                ? 'google'
                : provider === 'facebook'
                  ? 'facebook'
                  : provider === 'apple'
                    ? 'apple'
                    : 'email_otp'
            trackCompleteRegistration(method, generateEventId())
            try {
              const key = `jt_signup_tracked_${user.id}`
              if (!localStorage.getItem(key)) {
                trackGASignUp(method)
                localStorage.setItem(key, '1')
              }
            } catch { trackGASignUp(method) }
            trackLinkedInRegistration()
            if (user.email) {
              setNewsletterPromptEmail(user.email)
            }
          }
        }
      }
    )

    return () => {
      isMounted = false
      sub.subscription.unsubscribe()
    }
  }, [applyAvatarFromUser, setSessionHintCookie])

  const value: AuthContextValue = {
    sessionUserId,
    sessionEmail,
    avatarUrl,
    isAuthModalOpen,
    isAuthenticated: sessionUserId !== null,
    isAuthReady,
    newsletterPromptEmail,
    setIsAuthModalOpen,
    dismissNewsletterPrompt,
    handleLogout,
    handleDeleteAccount,
    setAvatarFromPath,
    refreshAvatar,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
